<template>
  <div class="container-fluid p-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="d-flex align-items-center">
            <button class="btn btn-outline-secondary me-3" @click="goBack">
              <i class="bi bi-arrow-left me-2"></i>Back
            </button>
            <h2 class="mb-0 fw-bold gradient-text">Task Details</h2>
          </div>
          <div class="d-flex gap-2">
            <button 
              class="btn btn-outline-primary" 
              @click="editTask"
              v-if="task && !task.completed"
            >
              <i class="bi bi-pencil me-2"></i>Edit
            </button>
            <button 
              class="btn btn-success" 
              @click="markAsComplete"
              v-if="task && !task.completed"
            >
              <i class="bi bi-check-lg me-2"></i>Mark Complete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading task details...</p>
    </div>

    <!-- Task Content -->
    <div v-else-if="task" class="row">
      <!-- Main Task Info -->
      <div class="col-lg-8 mb-4">
        <div class="modern-card p-4">
          <!-- Task Header -->
          <div class="d-flex align-items-start justify-content-between mb-4">
            <div>
              <h3 class="fw-bold mb-2">{{ task.title }}</h3>
              <div class="d-flex align-items-center gap-3 mb-3">
                <span 
                  class="badge" 
                  :class="getPriorityBadgeClass(task.priority)"
                >
                  {{ task.priority }} Priority
                </span>
                <span 
                  class="badge" 
                  :class="getStatusBadgeClass(task.status)"
                >
                  {{ task.status }}
                </span>
                <span class="text-muted">
                  <i class="bi bi-calendar me-1"></i>
                  Due: {{ formatDate(task.dueDate) }}
                </span>
              </div>
            </div>
            <div class="task-completion-status">
              <i 
                :class="task.completed ? 'bi bi-check-circle-fill text-success' : 'bi bi-circle text-muted'"
                style="font-size: 2rem;"
              ></i>
            </div>
          </div>

          <!-- Task Description -->
          <div class="mb-4">
            <h5 class="fw-bold mb-3">Description</h5>
            <p class="text-secondary">{{ task.description }}</p>
          </div>

          <!-- Task Instructions -->
          <div class="mb-4" v-if="task.instructions">
            <h5 class="fw-bold mb-3">Instructions</h5>
            <div class="instruction-list">
              <div 
                v-for="(instruction, index) in task.instructions" 
                :key="index"
                class="instruction-item d-flex align-items-start mb-2"
              >
                <span class="instruction-number me-3">{{ index + 1 }}</span>
                <span>{{ instruction }}</span>
              </div>
            </div>
          </div>

          <!-- Task Resources -->
          <div class="mb-4" v-if="task.resources && task.resources.length">
            <h5 class="fw-bold mb-3">Resources</h5>
            <div class="resource-list">
              <div 
                v-for="resource in task.resources" 
                :key="resource.id"
                class="resource-item modern-card p-3 mb-2 d-flex align-items-center"
              >
                <i :class="getResourceIcon(resource.type)" class="me-3 text-primary"></i>
                <div class="flex-grow-1">
                  <div class="fw-bold">{{ resource.title }}</div>
                  <small class="text-muted">{{ resource.description }}</small>
                </div>
                <button class="btn btn-outline-primary btn-sm" @click="openResource(resource)">
                  <i class="bi bi-box-arrow-up-right"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Progress Tracking -->
          <div class="mb-4" v-if="task.subtasks && task.subtasks.length">
            <h5 class="fw-bold mb-3">Progress</h5>
            <div class="progress mb-3">
              <div 
                class="progress-bar bg-success" 
                :style="{ width: getProgressPercentage() + '%' }"
              ></div>
            </div>
            <p class="text-muted mb-3">{{ getCompletedSubtasks() }} of {{ task.subtasks.length }} steps completed</p>
            
            <!-- Subtasks -->
            <div class="subtask-list">
              <div 
                v-for="subtask in task.subtasks" 
                :key="subtask.id"
                class="subtask-item d-flex align-items-center mb-2 p-2 rounded"
                :class="{ 'bg-light': subtask.completed }"
              >
                <input 
                  type="checkbox" 
                  class="form-check-input me-3"
                  v-model="subtask.completed"
                  @change="updateSubtaskProgress"
                >
                <span :class="{ 'text-decoration-line-through text-muted': subtask.completed }">
                  {{ subtask.title }}
                </span>
              </div>
            </div>
          </div>

          <!-- Comments Section -->
          <div class="mb-4">
            <h5 class="fw-bold mb-3">Comments & Notes</h5>
            <div class="comment-list mb-3">
              <div 
                v-for="comment in task.comments" 
                :key="comment.id"
                class="comment-item border-start border-primary ps-3 mb-3"
              >
                <div class="d-flex align-items-center mb-1">
                  <strong class="me-2">{{ comment.author }}</strong>
                  <small class="text-muted">{{ formatDate(comment.date) }}</small>
                </div>
                <p class="mb-0">{{ comment.text }}</p>
              </div>
            </div>
            
            <!-- Add Comment -->
            <div class="add-comment">
              <textarea 
                class="form-control mb-2" 
                rows="3" 
                placeholder="Add a comment or note..."
                v-model="newComment"
              ></textarea>
              <button class="btn btn-primary btn-sm" @click="addComment">
                <i class="bi bi-plus-lg me-2"></i>Add Comment
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <!-- Task Info -->
        <div class="modern-card p-4 mb-4">
          <h6 class="fw-bold mb-3">Task Information</h6>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Subject</small>
            <span class="fw-bold">{{ task.subject }}</span>
          </div>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Estimated Time</small>
            <span class="fw-bold">{{ task.estimatedTime }} minutes</span>
          </div>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Difficulty</small>
            <span class="fw-bold">{{ task.difficulty }}</span>
          </div>
          
          <div class="info-item mb-3">
            <small class="text-muted d-block">Created</small>
            <span class="fw-bold">{{ formatDate(task.createdDate) }}</span>
          </div>
          
          <div class="info-item" v-if="task.completedDate">
            <small class="text-muted d-block">Completed</small>
            <span class="fw-bold">{{ formatDate(task.completedDate) }}</span>
          </div>
        </div>

        <!-- Related Tasks -->
        <div class="modern-card p-4" v-if="relatedTasks.length">
          <h6 class="fw-bold mb-3">Related Tasks</h6>
          <div class="related-task-list">
            <div 
              v-for="relatedTask in relatedTasks" 
              :key="relatedTask.id"
              class="related-task-item p-2 mb-2 rounded border cursor-pointer"
              @click="viewTask(relatedTask.id)"
            >
              <div class="fw-bold">{{ relatedTask.title }}</div>
              <small class="text-muted">{{ relatedTask.subject }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="text-center py-5">
      <i class="bi bi-exclamation-triangle text-warning" style="font-size: 3rem;"></i>
      <h5 class="mt-3">Task Not Found</h5>
      <p class="text-muted">The requested task could not be loaded.</p>
      <button class="btn btn-primary" @click="goBack">Go Back</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TaskDetail',
  data() {
    return {
      loading: true,
      task: null,
      taskId: null,
      newComment: '',
      relatedTasks: []
    }
  },
  mounted() {
    this.taskId = this.$route.params.id || this.$route.query.taskId
    this.loadTaskDetails()
  },
  methods: {
    loadTaskDetails() {
      this.loading = true
      
      // Simulate API call
      setTimeout(() => {
        // Mock task data
        this.task = {
          id: this.taskId || '1',
          title: 'Complete Reading Assignment',
          description: 'Read the story "The Magic Garden" and answer the comprehension questions.',
          subject: 'Reading',
          priority: 'high',
          status: 'In Progress',
          difficulty: 'Intermediate',
          estimatedTime: 30,
          dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
          createdDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
          completed: false,
          completedDate: null,
          instructions: [
            'Open the story "The Magic Garden" from your library',
            'Read the story carefully, paying attention to characters and plot',
            'Answer the 5 comprehension questions at the end',
            'Submit your answers when complete'
          ],
          subtasks: [
            { id: 1, title: 'Read the story', completed: true },
            { id: 2, title: 'Answer question 1', completed: true },
            { id: 3, title: 'Answer question 2', completed: false },
            { id: 4, title: 'Answer question 3', completed: false },
            { id: 5, title: 'Submit answers', completed: false }
          ],
          resources: [
            {
              id: 1,
              type: 'story',
              title: 'The Magic Garden',
              description: 'Interactive story with illustrations',
              url: '/library/magic-garden'
            },
            {
              id: 2,
              type: 'worksheet',
              title: 'Comprehension Questions',
              description: 'Questions about the story',
              url: '/worksheets/magic-garden-questions'
            }
          ],
          comments: [
            {
              id: 1,
              author: 'Teacher Johnson',
              text: 'Great progress so far! Take your time with the questions.',
              date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
            }
          ]
        }
        
        this.relatedTasks = [
          { id: 2, title: 'Math Practice Sheet', subject: 'Math' },
          { id: 3, title: 'Science Observation', subject: 'Science' }
        ]
        
        this.loading = false
      }, 1000)
    },

    markAsComplete() {
      if (!this.task) return
      this.task.completed = true
      this.task.status = 'Completed'
      this.task.completedDate = new Date().toISOString()
      this.showMessage('Task marked as complete!')
    },

    editTask() {
      if (!this.task) return
      // Navigate to edit task page or open modal
      this.$router.push(`/tasks/${this.task.id}/edit`)
    },

    updateSubtaskProgress() {
      // Update progress when subtasks change
      this.$nextTick(() => {
        if (!this.task || !this.task.subtasks) return
        
        const completed = this.getCompletedSubtasks()
        const total = this.task.subtasks.length
        
        if (completed === total && !this.task.completed) {
          this.markAsComplete()
        }
      })
    },

    addComment() {
      if (!this.task || !this.newComment.trim()) return
      
      this.task.comments.push({
        id: Date.now(),
        author: 'Current User',
        text: this.newComment,
        date: new Date().toISOString()
      })
      this.newComment = ''
    },

    getProgressPercentage() {
      if (!this.task || !this.task.subtasks || this.task.subtasks.length === 0) return 0
      const completed = this.getCompletedSubtasks()
      return Math.round((completed / this.task.subtasks.length) * 100)
    },

    getCompletedSubtasks() {
      if (!this.task || !this.task.subtasks) return 0
      return this.task.subtasks.filter(subtask => subtask && subtask.completed).length
    },

    getPriorityBadgeClass(priority) {
      const classes = {
        high: 'bg-danger',
        medium: 'bg-warning',
        low: 'bg-success'
      }
      return classes[priority] || 'bg-secondary'
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Completed': 'bg-success',
        'In Progress': 'bg-primary',
        'Not Started': 'bg-secondary',
        'Overdue': 'bg-danger'
      }
      return classes[status] || 'bg-secondary'
    },

    getResourceIcon(type) {
      const icons = {
        story: 'bi bi-book',
        worksheet: 'bi bi-file-text',
        video: 'bi bi-play-circle',
        link: 'bi bi-link-45deg'
      }
      return icons[type] || 'bi bi-file'
    },

    openResource(resource) {
      // Navigate to resource or open in new tab
      if (resource.url.startsWith('http')) {
        window.open(resource.url, '_blank')
      } else {
        this.$router.push(resource.url)
      }
    },

    viewTask(taskId) {
      this.$router.push(`/tasks/${taskId}`)
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    goBack() {
      this.$router.go(-1)
    },

    showMessage(message, type = 'info') {
      // Create feedback alert
      const alertClass = type === 'success' ? 'alert-success' : 
                         type === 'warning' ? 'alert-warning' :
                         type === 'error' ? 'alert-danger' : 'alert-info';
      const alert = document.createElement('div');
      alert.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      alert.style.cssText = 'top: 20px; right: 20px; z-index: 9999; min-width: 300px;';
      alert.innerHTML = `
        <strong>${message}</strong>
        <button type="button" class="btn-close" onclick="this.parentElement.remove()"></button>
      `;
      document.body.appendChild(alert);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (alert.parentElement) {
          alert.remove();
        }
      }, 5000);
    }
  }
}
</script>

<style scoped>
.instruction-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: var(--role-primary);
  color: white;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: bold;
  flex-shrink: 0;
}

.subtask-item {
  transition: all 0.2s ease;
}

.subtask-item:hover {
  background-color: var(--bg-secondary) !important;
}

.related-task-item {
  transition: all 0.2s ease;
}

.related-task-item:hover {
  background-color: var(--role-light);
  border-color: var(--role-primary) !important;
}

.cursor-pointer {
  cursor: pointer;
}

.progress {
  height: 8px;
}

.comment-item {
  border-left-width: 3px !important;
}

.info-item small {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style> 